import type { ReactNode } from 'react';
import React, { createContext, useMemo } from 'react';
import { useParams } from 'react-router';

import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import { useJobSharedRenderingsQuery } from 'src/shared/graphql/generated';
import type {
  JobSharedRenderings,
  OrgBasicInfo,
} from 'src/shared/graphql/generated';

import { sanitizeRenderings } from '../helpers';
import type { RenderingContext } from '../types';
import { initialContext } from '../types';

const context = createContext<RenderingContext>(initialContext);

const Provider = ({ children }: { children: ReactNode }) => {
  // Provider used for the Renderings Hub un-authenticated, shared view
  const { shareToken } = useParams<{ shareToken: string }>();
  const { data, error, loading } = useJobSharedRenderingsQuery({
    variables: { shareToken },
  });

  const job = useMemo(
    () => (data?.jobSharedRenderings || {}) as JobSharedRenderings,
    [data],
  );
  const { captureRequest, hiDefRenderings, orgId, partnerId, userId, id } = job;
  const { orgBrandLogoUrl, customerDisplayName } =
    (captureRequest?.displayRequestingOrg || {}) as OrgBasicInfo;

  const noJobSharedRenderingFound =
    !loading && (!hiDefRenderings || hiDefRenderings?.length === 0);
  const otherError = !!error;

  const value = useMemo(() => {
    const trackingData = {
      feature: FeatureTracking.RENDERING,
      org_id: orgId ?? undefined,
      job_id: id ?? undefined,
      user_id: userId ?? undefined,
      rendering_id: hiDefRenderings?.length ? hiDefRenderings[0].id : undefined,
      partner_id: partnerId ?? undefined,
    };

    return {
      images: [],
      renderings: sanitizeRenderings(hiDefRenderings || []),
      jobId: id || undefined,
      job,
      orgId: orgId || undefined,
      partnerId: partnerId || undefined,
      userId: userId || undefined,
      orgName: customerDisplayName || '',
      orgLogoUrl: orgBrandLogoUrl || '',
      isCaptureRequest: !!captureRequest,
      trackingData,
      error: noJobSharedRenderingFound || otherError,
    };
  }, [
    id,
    orgId,
    partnerId,
    userId,
    hiDefRenderings,
    customerDisplayName,
    orgBrandLogoUrl,
    captureRequest,
    job,
    noJobSharedRenderingFound,
    otherError,
  ]);

  return <context.Provider value={value}>{children}</context.Provider>;
};
export { Provider, context };
